import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChefHat, MapPin, Heart, Clock, Users, ExternalLink, Github, Linkedin } from "lucide-react";

const Index = () => {
  const featuredRecipes = [
    {
      id: 1,
      title: "Traditional Lebanese Hummus",
      description: "Creamy, authentic hummus made with tahini and fresh lemon juice",
      image: "/images/middle_eastern_dishes_3.jpeg",
      cookTime: "15 min",
      difficulty: "Easy",
      cuisine: "Lebanese"
    },
    {
      id: 2,
      title: "Greek Moussaka",
      description: "Layers of eggplant, meat sauce, and béchamel in this classic Greek dish",
      image: "/images/middle_eastern_dishes_4.jpeg",
      cookTime: "2 hours",
      difficulty: "Medium",
      cuisine: "Greek"
    },
    {
      id: 3,
      title: "Turkish Baklava",
      description: "Flaky phyllo pastry layered with nuts and honey syrup",
      image: "/images/middle_eastern_dishes_1.jpeg",
      cookTime: "1 hour",
      difficulty: "Hard",
      cuisine: "Turkish"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary to-background">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <ChefHat className="h-8 w-8 text-primary" />
              <span className="text-2xl font-bold text-primary">Yorgo's Kitchen</span>
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="#home" className="text-foreground hover:text-primary transition-colors">Home</a>
              <a href="#about" className="text-foreground hover:text-primary transition-colors">About</a>
              <a href="#recipes" className="text-foreground hover:text-primary transition-colors">Recipes</a>
              <a href="#blog" className="text-foreground hover:text-primary transition-colors">Blog</a>
              <a href="#contact" className="text-foreground hover:text-primary transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-primary/10 text-primary border-primary/20">
                <MapPin className="w-4 h-4 mr-1" />
                Perth, Australia
              </Badge>
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                Authentic
                <span className="bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent"> Mediterranean </span>
                Flavors
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Welcome to Yorgo's Kitchen! I'm a Perth-based home chef with Greek and Lebanese heritage, 
                originally from Sydney. Join me as I share authentic recipes and stories from the Mediterranean.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary-glow hover:shadow-warm">
                  Explore Recipes
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-primary text-primary hover:bg-primary/5"
                  onClick={() => window.open('https://linktr.ee/yorgokattanfood', '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  All My Links
                </Button>
              </div>
              
              {/* Social Media Quick Links */}
              <div className="flex gap-3 mt-6">
                <a href="https://www.linkedin.com/in/yorgo-kattan-food-712128380" target="_blank" rel="noopener noreferrer" 
                   className="p-2 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="https://github.com/yorgokattan" target="_blank" rel="noopener noreferrer" 
                   className="p-2 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                  <Github className="w-5 h-5" />
                </a>
                <a href="https://yorgokattan.wordpress.com/" target="_blank" rel="noopener noreferrer" 
                   className="p-2 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-warm">
                <img 
                  src="/images/chef_cooking_1.webp" 
                  alt="Yorgo cooking in kitchen"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-lg">
                <div className="flex items-center space-x-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  <span className="font-semibold">1000+ Happy Cooks</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">About Yorgo Kattan</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Born in Sydney with Greek and Lebanese roots, now calling Perth home. 
              I'm passionate about preserving and sharing the authentic flavors of the Mediterranean.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center hover:shadow-warm transition-all duration-300">
              <CardHeader>
                <ChefHat className="w-12 h-12 text-primary mx-auto mb-4" />
                <CardTitle>Heritage Cooking</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Recipes passed down through generations, combining Greek and Lebanese traditions
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-warm transition-all duration-300">
              <CardHeader>
                <MapPin className="w-12 h-12 text-primary mx-auto mb-4" />
                <CardTitle>Sydney to Perth</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  From Sydney's multicultural food scene to Perth's fresh local ingredients
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-warm transition-all duration-300">
              <CardHeader>
                <Users className="w-12 h-12 text-primary mx-auto mb-4" />
                <CardTitle>Food Community</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Building connections through authentic Mediterranean cuisine and stories
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Recipes */}
      <section id="recipes" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Featured Recipes</h2>
            <p className="text-xl text-muted-foreground">
              Discover authentic Mediterranean dishes with step-by-step guides
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredRecipes.map((recipe) => (
              <Card key={recipe.id} className="overflow-hidden hover:shadow-warm transition-all duration-300 group">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={recipe.image} 
                    alt={recipe.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant="secondary">{recipe.cuisine}</Badge>
                    <Badge variant="outline">{recipe.difficulty}</Badge>
                  </div>
                  <CardTitle className="group-hover:text-primary transition-colors">{recipe.title}</CardTitle>
                  <CardDescription>{recipe.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>{recipe.cookTime}</span>
                    </div>
                    <Button variant="ghost" size="sm" className="text-primary hover:bg-primary/5">
                      View Recipe
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary/5">
              View All Recipes
            </Button>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary to-primary-glow">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Join the Mediterranean Kitchen
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get weekly recipes, cooking tips, and stories from my kitchen to yours
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-white/50"
            />
            <Button className="bg-white text-primary hover:bg-white/90 px-8">
              Subscribe
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-accent text-accent-foreground py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <ChefHat className="h-6 w-6" />
                <span className="text-xl font-bold">Yorgo's Kitchen</span>
              </div>
              <p className="text-sm opacity-80">
                Authentic Mediterranean recipes from a Perth-based home chef with Greek and Lebanese heritage.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#home" className="opacity-80 hover:opacity-100 transition-opacity">Home</a></li>
                <li><a href="#about" className="opacity-80 hover:opacity-100 transition-opacity">About</a></li>
                <li><a href="#recipes" className="opacity-80 hover:opacity-100 transition-opacity">Recipes</a></li>
                <li><a href="#blog" className="opacity-80 hover:opacity-100 transition-opacity">Blog</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Cuisines</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="opacity-80 hover:opacity-100 transition-opacity">Greek</a></li>
                <li><a href="#" className="opacity-80 hover:opacity-100 transition-opacity">Lebanese</a></li>
                <li><a href="#" className="opacity-80 hover:opacity-100 transition-opacity">Turkish</a></li>
                <li><a href="#" className="opacity-80 hover:opacity-100 transition-opacity">Mediterranean</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Connect</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="https://linktr.ee/yorgokattanfood" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity flex items-center gap-1">Linktree <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="https://www.tumblr.com/blog/yorgokattan" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity flex items-center gap-1">Tumblr <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="https://www.snapchat.com/@yorgokattanfood" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity flex items-center gap-1">Snapchat <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="https://www.linkedin.com/in/yorgo-kattan-food-712128380" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity flex items-center gap-1">LinkedIn <Linkedin className="w-3 h-3" /></a></li>
                <li><a href="https://yorgokattan.wordpress.com/" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity flex items-center gap-1">WordPress <ExternalLink className="w-3 h-3" /></a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">More Links</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="https://ko-fi.com/yorgokattan" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity flex items-center gap-1">Ko-fi <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="https://www.crunchbase.com/person/yorgo-kattan" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity flex items-center gap-1">Crunchbase <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="https://github.com/yorgokattan" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity flex items-center gap-1">GitHub <Github className="w-3 h-3" /></a></li>
                <li><a href="https://www.reddit.com/user/CuriousCod3786" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity flex items-center gap-1">Reddit <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="https://sites.google.com/view/yorgokattan/home" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity flex items-center gap-1">Google Sites <ExternalLink className="w-3 h-3" /></a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-sm opacity-80">
            <p>&copy; 2024 Yorgo's Kitchen. Made with ❤️ in Perth, Australia.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
